/**
 * WorkflowListPage — /agents/workflows
 * Entity list on the ListPage template (grid variant, one card per workflow). Demo: /tables/data-tables.
 */
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Play, Clock, FileText, Pencil, Archive, RotateCcw, Eye, EyeOff, Upload } from 'lucide-react';
import {
  ActionMenu,
  type ActionMenuItem,
  Badge,
  Button,
  Callout,
  Card,
  Chip,
  ConfirmDialog,
  EmptyState,
  PublishToMarketplaceModal,
  SkeletonList,
  Toolbar,
} from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';
import { ListPage } from '../../components/page';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Can } from '../../components/auth/Can.js';
import { EntitySearchInput } from '../../components/entity/EntitySearchInput.js';
import { PAGE_HELP } from '../../help';
import { usePolicy } from '../../hooks/usePolicy.js';
import type { EntitySearchResult } from '../../hooks/useEntitySearch.js';
import { CatalogAdoptionStatusPanel } from '../../components/catalog/CatalogAdoptionStatusPanel.js';
import {
  archiveWorkflowMutation,
  listCatalogVersionAdoptionsOptions,
  listWorkflowsOptions,
  listWorkflowsQueryKey,
  restoreWorkflowMutation,
  triggerWorkflowMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'warning',
  active: 'success',
  archived: 'neutral',
};

/** `schedule` crosses the wire as `unknown`; read its cron without asserting a shape. */
function scheduleCron(schedule: unknown): string | null {
  if (schedule && typeof schedule === 'object' && 'cron' in schedule) {
    const cron = (schedule as { cron?: unknown }).cron;
    if (typeof cron === 'string') return cron;
  }
  return null;
}

/** The ⋯ menu, or nothing when the person may take none of the row's actions. */
function RowActions({ items, 'aria-label': label }: { items: ActionMenuItem[]; 'aria-label': string }) {
  return items.length > 0 ? <ActionMenu items={items} aria-label={label} /> : null;
}

const newWorkflowAction = (
  <Can policy="CanManageWorkflows">
    <Link to="/agents/workflows/new" className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'md' })}>
      <Plus size={16} /> New workflow
    </Link>
  </Can>
);

export const WorkflowListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [publishTarget, setPublishTarget] = useState<{ id: string; name: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const canManageWorkflows = usePolicy('CanManageWorkflows');
  const canExecuteWorkflows = usePolicy('CanExecuteWorkflows');
  const { data: adoptionData, isLoading: adoptionsLoading } = useQuery(listCatalogVersionAdoptionsOptions());
  const adoptions = adoptionData?.data ?? [];

  // EntitySearchInput navigates on local result selection; marketplace rows
  // open the drawer (handled internally by EntitySearchInput).
  const handleSearchSelect = useCallback(
    (row: EntitySearchResult) => {
      navigate(`/agents/workflows/${row.id}`);
    },
    [navigate],
  );

  // A failed load must not render as "no workflows": it shows the alert with Retry.
  const { data: workflowData, isLoading: loading, error: loadError, refetch } = useQuery(listWorkflowsOptions());
  const workflows = workflowData?.data ?? [];

  const invalidateWorkflows = () => queryClient.invalidateQueries({ queryKey: listWorkflowsQueryKey() });

  const triggerMutation = useMutation({
    ...triggerWorkflowMutation(),
    onSuccess: () => {
      setActionError(null);
      void invalidateWorkflows();
    },
    onError: (err: Error) => setActionError(`Failed to trigger workflow: ${err.message}`),
  });

  const archiveMutation = useMutation({
    ...archiveWorkflowMutation(),
    onSuccess: () => {
      setActionError(null);
      void invalidateWorkflows();
    },
    onError: (err: Error) => setActionError(`Failed to archive workflow: ${err.message}`),
  });

  const restoreMutation = useMutation({
    ...restoreWorkflowMutation(),
    onSuccess: () => {
      setActionError(null);
      void invalidateWorkflows();
    },
    onError: (err: Error) => setActionError(`Failed to restore workflow: ${err.message}`),
  });

  // Archiving stops future runs, so the row menu asks first.
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; name: string; active: boolean } | null>(null);

  const archiveWarning = (name: string, isActive: boolean) =>
    isActive
      ? `"${name}" is active and scheduled. Archiving stops all future runs. Archive anyway?`
      : `Archive "${name}"? It will no longer run on schedule.`;

  // The row's actions sit in one ⋯ menu (owner 2026-10-17: row buttons are discreet; NextAdmin users table "Action"
  // column). Only actions the person may take are listed.
  const rowActions = (name: string, id: string, status: string | null | undefined): ActionMenuItem[] => {
    if (status === 'archived') {
      return canManageWorkflows
        ? [
            {
              id: 'restore',
              label: 'Restore',
              icon: <RotateCcw />,
              onAction: () => restoreMutation.mutate({ path: { id } }),
            },
          ]
        : [];
    }
    const items: ActionMenuItem[] = [];
    if (status === 'active' && canExecuteWorkflows) {
      items.push({
        id: 'run',
        label: 'Run now',
        icon: <Play />,
        onAction: () => triggerMutation.mutate({ path: { id }, body: {} }),
      });
    }
    if (canManageWorkflows) {
      items.push(
        { id: 'edit', label: 'Edit', icon: <Pencil />, onAction: () => navigate(`/agents/workflows/${id}/edit`) },
        {
          id: 'publish',
          label: 'Publish to marketplace',
          icon: <Upload />,
          onAction: () => setPublishTarget({ id, name }),
        },
        {
          id: 'archive',
          label: 'Archive',
          icon: <Archive />,
          tone: 'danger',
          onAction: () => setArchiveTarget({ id, name, active: status === 'active' }),
        },
      );
    }
    return items;
  };

  const filtered = showArchived ? workflows : workflows.filter((t) => t.status !== 'archived');

  const archivedCount = workflows.filter((t) => t.status === 'archived').length;
  // Workflows the org acquired from the catalog — the only workflow adoption the entity model tracks.
  const workflowAdoptions = adoptions.filter(
    (adoption) => adoption.ownerType === 'entity_acquisition' && adoption.targetType === 'workflow',
  );

  const alert = loadError ? (
    <Callout
      tone="danger"
      title="Workflows did not load"
      actions={
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      }
    >
      {loadError.message || 'Please try again.'}
    </Callout>
  ) : null;

  return (
    <ListPage
      variant="grid"
      title="Workflows"
      breadcrumbs={[{ label: 'Libraries', to: '/agents/libraries' }, { label: 'Workflows' }]}
      helpContent={PAGE_HELP.workflows}
      actions={
        <>
          {archivedCount > 0 && (
            <Button variant="ghost" onClick={() => setShowArchived(!showArchived)}>
              {showArchived ? <EyeOff size={14} /> : <Eye size={14} />}
              {showArchived ? 'Hide' : 'Show'} archived ({archivedCount})
            </Button>
          )}
          {newWorkflowAction}
        </>
      }
      alert={alert}
      toolbar={
        /* Search + scoped filters (entity-management guidelines §1.3). EntitySearchInput is a ranked typeahead
           overlay: selecting a result navigates directly. The grid below shows the full list for browsing. */
        <Toolbar
          search={
            <EntitySearchInput
              entityType="workflow"
              placeholder="Search workflows by name, description, tags…"
              onSelect={handleSearchSelect}
            />
          }
        />
      }
    >
      {actionError && <Callout tone="danger">{actionError}</Callout>}

      <CatalogAdoptionStatusPanel
        adoptions={workflowAdoptions}
        loading={adoptionsLoading}
        title="Workflow library updates"
        maxVisible={4}
      />

      {loading && <SkeletonList count={4} label="Loading workflows" />}

      <div className="grid gap-4">
        {!loading &&
          !loadError &&
          filtered.map((t) => {
            const rowAdoptions = workflowAdoptions.filter((adoption) => adoption.ownerId === Number(t.id));
            const id = String(t.id);
            return (
              <Card key={t.id} className={t.status === 'archived' ? 'opacity-60' : undefined}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <FileText size={20} className="text-accent-text" aria-hidden="true" />
                      <Link
                        to={`/agents/workflows/${t.id}`}
                        className="text-lg font-semibold text-heading transition hover:text-accent-text"
                      >
                        {t.name}
                      </Link>
                      <Badge variant={(t.status && STATUS_BADGE[t.status]) || 'neutral'}>{t.status ?? 'unknown'}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted">{t.description}</p>
                    <div className="mt-2 flex items-center gap-4 text-xs text-faint">
                      <span>v{t.version}</span>
                      <span>{t.stepCount} steps</span>
                      {t.schedule != null && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {scheduleCron(t.schedule) ?? 'Manual'}
                        </span>
                      )}
                    </div>
                    {(t.tags?.length ?? 0) > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {t.tags?.map((tag: string) => (
                          <Chip key={tag}>{tag}</Chip>
                        ))}
                      </div>
                    )}
                  </div>
                  <RowActions items={rowActions(t.name, id, t.status)} aria-label={`Actions for ${t.name}`} />
                </div>
                <CatalogAdoptionStatusPanel adoptions={rowAdoptions} compact maxVisible={2} />
              </Card>
            );
          })}

        {!loading &&
          !loadError &&
          filtered.length === 0 &&
          (showArchived ? (
            <div className="py-12 text-center text-muted">No workflows found.</div>
          ) : (
            <EmptyState
              icon={FileText}
              title={PAGE_HELP.workflows.emptyState.title}
              description={PAGE_HELP.workflows.emptyState.description}
              learnItems={PAGE_HELP.workflows.emptyState.learnItems}
              action={newWorkflowAction}
            />
          ))}
      </div>

      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={() => {
          if (archiveTarget) archiveMutation.mutate({ path: { id: archiveTarget.id } });
          setArchiveTarget(null);
        }}
        title="Archive workflow"
        message={archiveTarget ? archiveWarning(archiveTarget.name, archiveTarget.active) : ''}
        confirmLabel="Archive"
        tone="danger"
        loading={archiveMutation.isPending}
      />

      {publishTarget && (
        <PublishToMarketplaceModal
          entityType="workflow"
          entityId={publishTarget.id}
          entityName={publishTarget.name}
          open={!!publishTarget}
          onClose={() => setPublishTarget(null)}
        />
      )}
    </ListPage>
  );
};
